// AdditionalQueryPFCompact.jsx — versão PF, corporativa/compacta (sem provedores)
import { Checkbox, Label, Modal, ModalBody, ModalHeader } from "flowbite-react";
import { useQuery } from "react-query";
import { QUERIES } from "../../../config/apiConfig";
import axios from "../../../config/axiosInstance";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { useEffect, useMemo, useState } from "react";

/* Labels + descrições curtas (sem provedor) */
const defs = {
  // principais (títulos das colunas)
  "additional-avalia-max-pf": {
    label: "Avalia Max PF",
    description: "Visão ampla e enriquecida para análise PF.",
  },
  "additional-avalia-risco-pf": {
    label: "Avalia Risco PF",
    description: "Foco em risco, inadimplência e restrições.",
  },

  // insumos compartilhados (ficam listados em Max/Risco)
  "additional-avalia-cadastral-pf": {
    label: "Cadastral",
    description: "Dados PF consolidados e verificados.",
  },
  "additional-avalia-cadastral-pf-plus": {
    label: "Cadastral Plus",
    description: "Cadastral com fontes adicionais.",
  },
  "additional-avalia-score-positivo-pf": {
    label: "Score Positivo",
    description: "Indicadores positivos de comportamento.",
  },
  "additional-avalia-renda-presumida-pf": {
    label: "Renda Presumida",
    description: "Estimativa baseada em sinais de mercado.",
  },
  "additional-avalia-renda-presumida-pf-plus": {
    label: "Renda Presumida Plus",
    description: "Estimativa com cobertura ampliada.",
  },
  "additional-protesto-nacional-pf": {
    label: "Protesto Nacional",
    description: "Cobertura nacional de protestos.",
  },
};

const sharedInsumosPF = [
  "additional-avalia-cadastral-pf",
  "additional-avalia-cadastral-pf-plus",
  "additional-avalia-score-positivo-pf",
  "additional-avalia-renda-presumida-pf",
  "additional-avalia-renda-presumida-pf-plus",
  "additional-protesto-nacional-pf",
];

/** Item compacto (checkbox à esquerda, label à direita) */
function LineCheck({ url, checked, onChange }) {
  const d = defs[url] || {};
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={url}
        checked={checked}
        onChange={(e) => onChange(url, e.target.checked)}
        className="scale-90"
      />
      <Label
        htmlFor={url}
        className="text-[13px] font-medium text-slate-800"
        title={d.description || ""}
      >
        {d.label || url}
      </Label>
    </div>
  );
}

export default function AdditionalQueryPFCompact({
  selectedQueries,
  setSelectedQueries,
  type = "F",
}) {
  const [openModal, setOpenModal] = useState(false);

  // estados por grupo
  const [selectedPrincipais, setSelectedPrincipais] = useState([]);
  const [selectedMax, setSelectedMax] = useState([]);
  const [selectedRisco, setSelectedRisco] = useState([]);

  // busca o que a empresa realmente tem disponível (PF)
  const { data: queryAdditionals = [] } = useQuery(
    ["queryAdditionals", type],
    async () => {
      const { data } = await axios.get(
        `${QUERIES.GET_ADDITIONALS_BY_COMPANY}?type=${type}`
      );
      return data.data;
    }
  );

  const availableUrls = useMemo(
    () => new Set(queryAdditionals.map((q) => q.url)),
    [queryAdditionals]
  );

  // prefill a partir do pai
  useEffect(() => {
    if (!Array.isArray(selectedQueries) || selectedQueries.length === 0) return;
    const has = (u) => selectedQueries.includes(u);

    setSelectedPrincipais(
      ["additional-avalia-max-pf", "additional-avalia-risco-pf"].filter(has)
    );
    setSelectedMax(sharedInsumosPF.filter(has));
    setSelectedRisco(sharedInsumosPF.filter(has)); // espelha se o pai trouxe
  }, []); // mount only

  // sobe consolidação ao pai
  useEffect(() => {
    const all = Array.from(
      new Set([...selectedPrincipais, ...selectedMax, ...selectedRisco])
    );
    setSelectedQueries(all);
  }, [selectedPrincipais, selectedMax, selectedRisco, setSelectedQueries]);

  /* toggles */
  const togglePrincipal = (id, isChecked) => {
    setSelectedPrincipais((prev) =>
      isChecked ? [...prev, id] : prev.filter((v) => v !== id)
    );

    // regra: marcar Risco garante Protesto Nacional
    if (id === "additional-avalia-risco-pf" && isChecked) {
      if (!selectedRisco.includes("additional-protesto-nacional-pf")) {
        setSelectedRisco((prev) => [
          ...prev,
          "additional-protesto-nacional-pf",
        ]);
      }
    }
  };
  const toggleMax = (url, isChecked) =>
    setSelectedMax((prev) =>
      isChecked ? [...prev, url] : prev.filter((v) => v !== url)
    );
  const toggleRisco = (url, isChecked) => {
    let updated = isChecked
      ? [...selectedRisco, url]
      : selectedRisco.filter((v) => v !== url);
    if (
      url === "additional-avalia-risco-pf" &&
      isChecked &&
      !updated.includes("additional-protesto-nacional-pf")
    ) {
      updated.push("additional-protesto-nacional-pf");
    }
    setSelectedRisco(updated);
  };

  // visíveis pelo que a empresa possui
  const vShared = sharedInsumosPF.filter((u) => availableUrls.has(u));
  const show = availableUrls.size > 0 && vShared.length > 0;

  if (!show) return null;

  return (
    <>
      {/* Cabeçalho enxuto */}
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-900">
          Adicionais de Consulta — PF
        </h3>
        <AiOutlineQuestionCircle
          className="h-4 w-4 cursor-pointer text-slate-500 hover:text-slate-700"
          onClick={() => setOpenModal(true)}
          title="Como funcionam os adicionais"
        />
      </div>
      <p className="mb-4 text-[12px] text-slate-600">
        Selecione os módulos necessários. Interface compacta, sem distrações.
      </p>

      {/* Linha única: Pacote Max / Pacote Risco */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Max */}
        <section className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-md">
          <div className="mb-2 flex items-start gap-2">
            <Checkbox
              id="additional-avalia-max-pf"
              checked={selectedPrincipais.includes("additional-avalia-max-pf")}
              onChange={(e) =>
                togglePrincipal("additional-avalia-max-pf", e.target.checked)
              }
              className="scale-95"
            />
            <div className="flex-1 -mt-1">
              <Label
                htmlFor="additional-avalia-max-pf"
                className="text-[14px] font-semibold text-slate-900"
              >
                {defs["additional-avalia-max-pf"].label}
              </Label>
              <div className="text-[11px] text-slate-500">
                {defs["additional-avalia-max-pf"].description}
              </div>
            </div>
          </div>

          {/* lista densa em 2 colunas */}
          <div className="grid grid-cols-1 gap-y-1 sm:grid-cols-2">
            {vShared.map((url) => (
              <LineCheck
                key={`max-${url}`}
                url={url}
                checked={selectedMax.includes(url)}
                onChange={toggleMax}
              />
            ))}
          </div>
        </section>

        {/* Risco */}
        <section className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-md">
          <div className="mb-2 flex items-start gap-2">
            <Checkbox
              id="additional-avalia-risco-pf"
              checked={selectedPrincipais.includes(
                "additional-avalia-risco-pf"
              )}
              onChange={(e) =>
                togglePrincipal("additional-avalia-risco-pf", e.target.checked)
              }
              className="scale-95"
            />
            <div className="flex-1 -mt-1">
              <Label
                htmlFor="additional-avalia-risco-pf"
                className="text-[14px] font-semibold text-slate-900"
              >
                {defs["additional-avalia-risco-pf"].label}
              </Label>
              <div className="text-[11px] text-slate-500">
                {defs["additional-avalia-risco-pf"].description}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-y-1 sm:grid-cols-2">
            {vShared.map((url) => (
              <LineCheck
                key={`risco-${url}`}
                url={url}
                checked={selectedRisco.includes(url)}
                onChange={toggleRisco}
              />
            ))}
          </div>
        </section>
      </div>

      {/* Modal de ajuda, discreto */}
      <Modal show={openModal} onClose={() => setOpenModal(false)}>
        <ModalHeader>Adicionais — Como usar</ModalHeader>
        <ModalBody>
          <div className="space-y-2 text-sm text-slate-700">
            <p>
              Marque apenas o que agrega valor à sua análise. Os pacotes{" "}
              <strong>Max</strong> e <strong>Risco</strong> permitem combinar
              insumos rapidamente.
            </p>
            <p>
              Ao ativar <strong>Pacote Risco PF</strong>, o{" "}
              <em>Protesto Nacional</em> é incluído automaticamente para
              garantir o mínimo de cobertura.
            </p>
            <p>
              Os insumos listados são densos e objetivos para acelerar a
              decisão, mantendo a interface limpa.
            </p>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
